/**
 * "Somebody is ringing this clinician" — the fact, its one owner, and a name
 * for every way it stops being true.
 *
 * WHY A STORE AND NOT A MOUNTED COMPONENT. A ring that begins because a dialog
 * mounts and ends because it unmounts has its lifecycle spread across the
 * subscription, the dialog and the overlay host, and it cannot answer "why did
 * it stop" at all. `beginRing` and `endRing` below are the ONLY two writers:
 * the subscription calls them, the dialog's buttons call them, the age rule
 * calls them. A ring tone is a later PR that subscribes here and changes
 * nothing else — which is the whole reason the seam is shaped this way.
 *
 * WHAT IS DELIBERATELY NOT HERE: an audio element, an asset, a settings toggle,
 * or a placeholder for one. An empty seam is worse than no seam. The one thing
 * left for that PR is `overActiveCall`, recorded below because the decision it
 * feeds — do not play a tone over a consult — has to be made from the state at
 * the moment the ring arrived, and because browser autoplay unlocking belongs
 * to the app shell rather than to this feature.
 *
 * THE OVERLAY IS THE RENDERING, NOT THE FACT. The ring is a transient
 * notification with plain-data props, so it goes on the app's overlay stack
 * like every other one. This module opens and closes that entry, so the stack
 * follows the fact rather than competing with it.
 */

import { type Call, isRingStale, RING_MAX_AGE_MS } from "@repo/conferencing";
import { callStore, mediaReadiness } from "@repo/conferencing/components";
import { createStore } from "@xstate/store";
import { useSelector } from "@xstate/store-react";
// The overlay STORE, not the overlay barrel. The barrel pulls the registry,
// which imports this feature's dialog; going through it would close a module
// cycle that throws at init. `store.ts` imports the registry for types only.
import { closeOverlay, openOverlay } from "@/app/overlays/store";

/**
 * Why a ring stopped. Told apart because they mean different things to the
 * person who was being rung, and because a tone's fade differs by ending.
 */
export type RingEndReason =
  /** They picked up. */
  | "answered"
  /** They said no. */
  | "declined"
  /** It stopped ringing elsewhere — answered on another device, or hung up. */
  | "withdrawn"
  /** Nobody answered within `RING_MAX_AGE_MS`. */
  | "rangOut"
  /** A second colleague rang while this one was still on screen. */
  | "superseded";

export interface IncomingRing {
  readonly call: Call;
  /** The overlay entry this ring is being rendered as. */
  readonly overlayId: string;
  /**
   * Whether the clinician was already in a room when it arrived. Recorded at
   * arrival rather than read later, because that is the instant the question
   * is actually about.
   */
  readonly overActiveCall: boolean;
}

interface RingContext {
  ring: IncomingRing | undefined;
  /** The last ending, kept so a subscriber can react to WHY, not just to gone. */
  lastEnded: { callId: string; reason: RingEndReason } | undefined;
}

export const ringStore = createStore({
  context: { ring: undefined, lastEnded: undefined } as RingContext,
  on: {
    begin: (ctx, event: { ring: IncomingRing }) => ({ ...ctx, ring: event.ring }),
    end: (_ctx, event: { callId: string; reason: RingEndReason }) => ({
      ring: undefined,
      lastEnded: { callId: event.callId, reason: event.reason },
    }),
  },
});

/**
 * The age rule enforced as a timer rather than a render. A ring on screen after
 * `RING_MAX_AGE_MS` offers an answer the server has already stopped honouring,
 * and nothing re-renders a dialog just because time passed.
 */
let rangOutTimer: ReturnType<typeof setTimeout> | undefined;

function stopTimer(): void {
  if (rangOutTimer !== undefined) clearTimeout(rangOutTimer);
  rangOutTimer = undefined;
}

/** Start ringing. Idempotent for the ring already showing. */
export function beginRing(call: Call): void {
  const current = ringStore.get().context.ring;
  if (current?.call.id === call.id) return;
  if (current) endRing(current.call.id, "superseded");

  // Already rung out by the time it reached us — a reconnect replaying, or a
  // slow hop. Nothing to show, and nothing to name an ending for.
  if (isRingStale(call, new Date())) return;

  void mediaReadiness.prepare();
  const overlayId = openOverlay("call.ring", { callId: call.id });
  ringStore.send({
    type: "begin",
    ring: { call, overlayId, overActiveCall: callStore.get().context.active !== undefined },
  });

  stopTimer();
  const remaining = call.ringingSince.getTime() + RING_MAX_AGE_MS - Date.now();
  rangOutTimer = setTimeout(() => endRing(call.id, "rangOut"), Math.max(remaining, 0));
}

/**
 * Stop ringing, and say why.
 *
 * Keyed, for the same reason `leaveCall` is: a late notification about a call
 * that has already been replaced must not take the current ring down with it.
 */
export function endRing(callId: string, reason: RingEndReason): void {
  const current = ringStore.get().context.ring;
  if (current?.call.id !== callId) return;

  if (reason !== "answered") mediaReadiness.cancelPreparation();
  stopTimer();
  // Close, not dismiss: the exit animation runs and the overlay host drops the
  // entry when it finishes.
  closeOverlay(current.overlayId);
  ringStore.send({ type: "end", callId, reason });
}

/** The ring on screen, or `undefined`. */
export const useIncomingRing = (): IncomingRing | undefined => useSelector(ringStore, (s) => s.context.ring);
